import { useEffect, useMemo, useRef, useState } from "react";
import InventoryService from "services/InventoryService";
import { InventoryGrouping } from "models/InventoryModel";
import { DemandSettingsByInventory, WHAT_IF_STATUS, WhatIfSimulation, unsavedInventoryIds, whatIfFor } from "./demandWhatIf";

// Let typing settle before asking the server
const WHAT_IF_SETTLE_MS = 600;

type ForecastSettings = { startDate: string; endDate: string; [forecastSetting: string]: unknown };

/**
 * Runs a what-if forecast for every product row ON SCREEN that has unsaved demand adjustments or a
 * trial daily rate, and keeps the answers by grouping key. Rows off screen are simulated when they
 * come into view. Nothing here saves anything.
 */
export function useDemandWhatIf({ inventoryService, forecastSettings, dataVersion, visibleGroupings, current, saved }: {
  inventoryService: InventoryService;
  /** The settings the page's /forecasts request used, so the what-if is comparable with the table */
  forecastSettings: ForecastSettings;
  /** Changes whenever the page's forecast data is reloaded: earlier simulations are then out of date */
  dataVersion: number;
  visibleGroupings: InventoryGrouping[];
  current: DemandSettingsByInventory;
  saved: DemandSettingsByInventory;
}) {
  const [trialRatesByKey, setTrialRatesByKey] = useState<Record<string, number>>({});
  const [simulationsByKey, setSimulationsByKey] = useState<Record<string, WhatIfSimulation>>({});
  // The request last sent per row; an answer is kept only if it is still the one being waited for
  const requestedSignatureByKey = useRef<Record<string, string>>({});

  const unsavedIds = useMemo(() => unsavedInventoryIds(current, saved), [current, saved]);

  const targets = useMemo(() => {
    const settingsSignature = JSON.stringify([forecastSettings, dataVersion]);
    return visibleGroupings.flatMap(grouping => {
      const whatIf = whatIfFor(grouping, current, unsavedIds, trialRatesByKey);
      return whatIf ? [{ grouping, whatIf, signature: settingsSignature + JSON.stringify(whatIf) }] : [];
    });
  }, [visibleGroupings, current, unsavedIds, trialRatesByKey, forecastSettings, dataVersion]);

  useEffect(() => {
    const targetKeys = new Set(targets.map(target => target.grouping.key));
    for (const key of Object.keys(requestedSignatureByKey.current)) {
      if (!targetKeys.has(key)) delete requestedSignatureByKey.current[key];
    }
    const outdated = targets.filter(target => requestedSignatureByKey.current[target.grouping.key] !== target.signature);

    setSimulationsByKey(previous => {
      const next: Record<string, WhatIfSimulation> = {};
      for (const { grouping, whatIf } of targets) {
        const isOutdated = outdated.some(target => target.grouping.key === grouping.key);
        next[grouping.key] = isOutdated
          ? { status: WHAT_IF_STATUS.LOADING, whatIf, result: previous[grouping.key]?.result }
          : previous[grouping.key];
      }
      return next;
    });
    if (outdated.length === 0) return;

    const timeoutId = setTimeout(() => {
      for (const { grouping, whatIf, signature } of outdated) {
        requestedSignatureByKey.current[grouping.key] = signature;
        const settle = (simulation: WhatIfSimulation) => {
          if (requestedSignatureByKey.current[grouping.key] !== signature) return; // superseded, or no longer a what-if
          setSimulationsByKey(previous => ({ ...previous, [grouping.key]: simulation }));
        };
        inventoryService.simulateForecast({ ...forecastSettings, inventoryId: grouping.inventory_id, country: grouping.country, whatIf })
          .then(result => settle({ status: WHAT_IF_STATUS.READY, whatIf, result }))
          .catch((error: Error) => settle({ status: WHAT_IF_STATUS.ERROR, whatIf, error: error.message }));
      }
    }, WHAT_IF_SETTLE_MS);
    return () => clearTimeout(timeoutId);
  }, [targets, inventoryService, forecastSettings]);

  const setTrialRate = (groupingKey: string, unitsPerDay: number | null) => {
    setTrialRatesByKey(previous => {
      const { [groupingKey]: _removed, ...others } = previous;
      return unitsPerDay === null ? others : { ...others, [groupingKey]: unitsPerDay };
    });
  };

  return { simulationsByKey, unsavedIds, trialRatesByKey, setTrialRate };
}
